import { CartItemUpdateInput, OpenTabCartFragment } from '@/features/graphql/generated/graphql'
import { AccountItemView } from '@/features/openTabs/components/AccountItemCard'
import { formatAccountPrice } from '@/features/openTabs/utils/viewModels'
import { formatProductName } from '@/features/pos/components/Cart/utils/formats'
import { formatFreeOfChargeAblePrice } from '@/utils/formatFreeOfChargeAblePrice'
import { TFunction } from 'i18next'

type DraftCartItem = OpenTabCartFragment['items'][number]

export const toDraftCartItemUpdate = (item: DraftCartItem): CartItemUpdateInput => ({
    cartItemUuid: item.uuid,
    productVariantUuid: item.productVariant.uuid,
    quantity: item.quantity,
    toppingGroupToppings: item.toppingGroupToppings.map((topping) => ({
        toppingGroupUuid: topping.toppingGroup.uuid,
        toppingUuid: topping.topping.uuid,
        quantity: topping.quantity,
    })),
    additionGroupProductVariants: item.additionGroupProductVariants.map((addition) => ({
        additionGroupUuid: addition.additionGroup.uuid,
        productVariantUuid: addition.productVariant.uuid,
        quantity: addition.quantity,
    })),
    removedRecipeIngredients: item.removedRecipeIngredients.map((ingredient) => ingredient.uuid),
})

const formatModifierName = (
    name: string,
    quantity: number,
    amount: string,
    currency: string,
    t: TFunction<'app'>
) => `${quantity}x ${name} (${formatFreeOfChargeAblePrice({ amount, currency }, t, false, 2)})`

export const toDraftCartItemView = (item: DraftCartItem, t: TFunction<'app'>): AccountItemView => {
    const itemCurrency = item.totalProductPrice.currency

    return {
        id: item.uuid,
        name: `${formatProductName(item.productVariant.product.name, item.productVariant.name)} (${formatAccountPrice(item.productVariant.price.amount, item.productVariant.price.currency)})`,
        quantity: item.quantity,
        canRemove: true,
        totalPrice: formatAccountPrice(item.totalProductPrice.amount, itemCurrency),
        additions: [
            ...item.toppingGroupToppings.map((topping) => ({
                id: `topping-${topping.toppingGroup.uuid}-${topping.topping.uuid}`,
                label: formatModifierName(
                    topping.topping.name,
                    topping.quantity,
                    topping.toppingGroup.freeOfCharge ? '0' : topping.topping.price.amount,
                    topping.topping.price.currency,
                    t
                ),
            })),
            ...item.additionGroupProductVariants.map((addition) => ({
                id: `addition-${addition.uuid}`,
                label: formatModifierName(
                    formatProductName(
                        addition.productVariant.product.name,
                        addition.productVariant.name,
                        'parentheses'
                    ),
                    addition.quantity,
                    addition.additionGroup.freeOfCharge
                        ? '0'
                        : addition.productVariant.price.amount,
                    addition.productVariant.price.currency,
                    t
                ),
            })),
        ],
        removals: item.removedRecipeIngredients.map((ingredient) => ingredient.name),
    }
}
